import React, { useState, useEffect, useCallback } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Film, Upload, Download, Loader2, Play, FileVideo, Clock, Layers,
  Settings, Wifi, WifiOff, Send, Monitor, Trash2, RefreshCw, Sparkles, Clapperboard
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// ================= TAB: GENERATORE SLIDE =================
function GeneratorTab() {
  const [text, setText] = useState("");
  const [logo, setLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [slideDuration, setSlideDuration] = useState(3);
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [videos, setVideos] = useState([]);
  const [error, setError] = useState("");

  const fetchVideos = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/video-generator/list`);
      const data = await res.json();
      setVideos(data.videos || []);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchVideos(); }, [fetchVideos]);

  const handleGenerate = async () => {
    if (!text.trim()) { setError("Inserisci il testo"); return; }
    setError(""); setGenerating(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("text", text);
      fd.append("slide_duration", slideDuration);
      if (logo) fd.append("logo", logo);
      const res = await fetch(`${API}/api/video-generator/generate`, { method: "POST", body: fd });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || "Errore"); }
      const data = await res.json();
      setResult(data); fetchVideos();
    } catch (e) { setError(e.message); }
    finally { setGenerating(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-5">
        <Card className="bg-[#12122a] border-gray-800">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg text-gray-200 flex items-center gap-2">
              <Layers className="w-5 h-5 text-violet-400" /> Contenuto Video
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm text-gray-400 mb-1.5 block">Testo per le slide</label>
              <Textarea data-testid="video-text-input" value={text} onChange={(e) => setText(e.target.value)}
                placeholder="Scrivi il testo che vuoi trasformare in video..."
                className="min-h-[140px] bg-[#0d0d20] border-gray-700 text-white placeholder:text-gray-600 resize-none focus:border-violet-500" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-400 mb-1.5 block">Logo (opzionale)</label>
                <label data-testid="logo-upload-btn" className="flex items-center gap-2 px-4 py-2.5 bg-[#0d0d20] border border-gray-700 rounded-lg cursor-pointer hover:border-violet-500 transition-colors text-sm">
                  <Upload className="w-4 h-4 text-violet-400" />
                  <span className="text-gray-300">{logo ? logo.name : "Carica logo"}</span>
                  <input type="file" accept="image/*" onChange={(e) => {
                    const f = e.target.files[0];
                    if (f) { setLogo(f); setLogoPreview(URL.createObjectURL(f)); }
                  }} className="hidden" />
                </label>
                {logoPreview && <img src={logoPreview} alt="Logo" className="w-10 h-10 mt-2 rounded object-contain bg-gray-800 p-0.5" />}
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-1.5 block flex items-center gap-1.5">
                  <Clock className="w-3.5 h-3.5" /> Durata slide (sec)
                </label>
                <Input data-testid="slide-duration-input" type="number" min={1} max={10} value={slideDuration}
                  onChange={(e) => setSlideDuration(Number(e.target.value))}
                  className="bg-[#0d0d20] border-gray-700 text-white w-24 focus:border-violet-500" />
              </div>
            </div>
            {error && <div data-testid="video-error" className="text-red-400 text-sm bg-red-900/20 border border-red-800/50 rounded-lg px-4 py-2.5">{error}</div>}
            <Button data-testid="generate-video-btn" onClick={handleGenerate} disabled={generating || !text.trim()}
              className="w-full bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 text-white py-3 text-base font-semibold">
              {generating ? <span className="flex items-center gap-2"><Loader2 className="w-5 h-5 animate-spin" /> Generazione...</span>
                : <span className="flex items-center gap-2"><Film className="w-5 h-5" /> Genera Video</span>}
            </Button>
          </CardContent>
        </Card>
        {result && (
          <Card className="bg-[#12122a] border-gray-800" data-testid="video-result">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg text-green-400 flex items-center gap-2"><Play className="w-5 h-5" /> Video Generato</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="rounded-xl overflow-hidden bg-black">
                <video data-testid="video-player" controls className="w-full max-h-[400px]" src={`${API}${result.download_url}`} />
              </div>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <span className="text-sm text-gray-400">{result.slides_count} slide</span>
                <a data-testid="download-video-btn" href={`${API}${result.download_url}`} download
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-violet-600 hover:bg-violet-700 rounded-lg text-sm font-medium transition-colors">
                  <Download className="w-4 h-4" /> Scarica MP4
                </a>
              </div>
              {result.slides_text && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                  {result.slides_text.map((s, i) => (
                    <div key={i} className="text-xs text-gray-400 bg-[#0d0d20] rounded-lg px-3 py-2 border border-gray-800">
                      <span className="text-violet-400 font-medium mr-1.5">#{i + 1}</span>{s}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
      <div>
        <Card className="bg-[#12122a] border-gray-800">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg text-gray-200 flex items-center gap-2">
              <FileVideo className="w-5 h-5 text-fuchsia-400" /> Libreria Video
            </CardTitle>
          </CardHeader>
          <CardContent>
            {videos.length === 0 ? <p className="text-gray-500 text-sm text-center py-8">Nessun video</p> : (
              <div className="space-y-2.5 max-h-[500px] overflow-y-auto pr-1">
                {videos.map((v, i) => (
                  <div key={v.filename} data-testid={`video-item-${i}`}
                    className="flex items-center justify-between bg-[#0d0d20] border border-gray-800 rounded-lg px-3 py-2.5 hover:border-gray-700 transition-colors">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <FileVideo className="w-4 h-4 text-fuchsia-400 shrink-0" />
                      <div className="min-w-0">
                        <p className="text-xs text-gray-300 truncate max-w-[140px]">{v.filename}</p>
                        <p className="text-[10px] text-gray-600">{v.size_mb} MB</p>
                      </div>
                    </div>
                    <a href={`${API}${v.download_url}`} download className="text-violet-400 hover:text-violet-300">
                      <Download className="w-4 h-4" />
                    </a>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ================= TAB: KDENLIVE EDITOR =================
function KdenliveTab() {
  const [bridgeUrl, setBridgeUrl] = useState("");
  const [bridgeStatus, setBridgeStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [instructions, setInstructions] = useState("");
  const [processing, setProcessing] = useState(false);
  const [editResult, setEditResult] = useState(null);
  const [bridgeFiles, setBridgeFiles] = useState({ uploads: [], outputs: [] });
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const fetchBridgeConfig = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/video-editor/config/bridge`);
      const text = await res.text();
      try {
        const data = JSON.parse(text);
        setBridgeUrl(data.bridge_url || "");
        setBridgeStatus(data);
      } catch (pe) {
        setBridgeStatus({ status: "error" });
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  const fetchBridgeFiles = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/video-editor/bridge/files`);
      if (res.ok) {
        const data = await res.json();
        setBridgeFiles(data);
      }
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchBridgeConfig(); }, [fetchBridgeConfig]);
  useEffect(() => {
    if (bridgeStatus?.status === "connected") fetchBridgeFiles();
  }, [bridgeStatus, fetchBridgeFiles]);

  const saveBridge = async () => {
    if (!bridgeUrl.trim()) return;
    setSaving(true); setError("");
    try {
      const res = await fetch(`${API}/api/video-editor/config/bridge`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ bridge_url: bridgeUrl })
      });
      const text = await res.text();
      if (!res.ok) {
        try { const e = JSON.parse(text); throw new Error(e.detail || "Errore connessione"); }
        catch (pe) { if (pe instanceof SyntaxError) throw new Error("Errore connessione al bridge"); throw pe; }
      }
      await fetchBridgeConfig();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) {
      setError("File troppo grande per upload via browser (max 50MB). Usa 'Aggiungi da PC' con il percorso locale.");
      return;
    }
    setUploading(true); setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${API}/api/video-editor/bridge/upload`, { method: "POST", body: fd });
      const text = await res.text();
      if (!res.ok) { try { throw new Error(JSON.parse(text).detail); } catch(pe) { if (pe instanceof SyntaxError) throw new Error("Errore upload"); throw pe; } }
      fetchBridgeFiles();
    } catch (e) { setError(e.message); }
    finally { setUploading(false); }
  };

  const [localPath, setLocalPath] = useState("");
  const [copying, setCopying] = useState(false);
  const [browseDir, setBrowseDir] = useState("D:\\");
  const [browseFiles, setBrowseFiles] = useState([]);
  const [showBrowser, setShowBrowser] = useState(false);

  const handleCopyLocal = async () => {
    if (!localPath.trim()) return;
    setCopying(true); setError("");
    try {
      const res = await fetch(`${API}/api/video-editor/bridge/copy-local`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ path: localPath })
      });
      const text = await res.text();
      if (!res.ok) { try { throw new Error(JSON.parse(text).detail); } catch(pe) { if (pe instanceof SyntaxError) throw new Error("Errore copia"); throw pe; } }
      setLocalPath("");
      fetchBridgeFiles();
    } catch (e) { setError(e.message); }
    finally { setCopying(false); }
  };

  const handleBrowse = async (dir) => {
    try {
      const res = await fetch(`${API}/api/video-editor/bridge/browse?dir=${encodeURIComponent(dir)}`);
      if (res.ok) {
        const data = await res.json();
        setBrowseDir(data.directory || dir);
        setBrowseFiles(data.files || []);
        setShowBrowser(true);
      }
    } catch (e) { console.error(e); }
  };

  const selectBrowseFile = (filePath) => {
    setLocalPath(filePath);
    setShowBrowser(false);
  };

  const handleAIEdit = async () => {
    if (!instructions.trim()) return;
    setProcessing(true); setEditResult(null); setError("");
    try {
      const res = await fetch(`${API}/api/video-editor/ai/edit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ instructions })
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || "Errore"); }
      const data = await res.json();
      setEditResult(data);
      fetchBridgeFiles();
    } catch (e) { setError(e.message); }
    finally { setProcessing(false); }
  };

  const isConnected = bridgeStatus?.status === "connected";

  return (
    <div className="space-y-6">
      {/* Bridge Connection */}
      <Card className="bg-[#12122a] border-gray-800">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg text-gray-200 flex items-center gap-2">
            <Settings className="w-5 h-5 text-amber-400" /> Connessione Kdenlive Bridge
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-2 mb-3">
            {loading ? <Loader2 className="w-4 h-4 animate-spin text-gray-400" /> :
              isConnected ? <><Wifi className="w-4 h-4 text-green-400" /><span className="text-green-400 text-sm font-medium">Connesso</span></>
                : <><WifiOff className="w-4 h-4 text-red-400" /><span className="text-red-400 text-sm font-medium">Non connesso</span></>}
          </div>
          <div className="flex gap-2">
            <Input data-testid="bridge-url-input" value={bridgeUrl} onChange={(e) => setBridgeUrl(e.target.value)}
              placeholder="https://xxxx.ngrok-free.app (URL del bridge)"
              className="bg-[#0d0d20] border-gray-700 text-white flex-1 focus:border-amber-500" />
            <Button onClick={saveBridge} disabled={saving || !bridgeUrl.trim()}
              className="bg-amber-600 hover:bg-amber-700 text-white px-6">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Connetti"}
            </Button>
          </div>

          {isConnected && bridgeStatus?.bridge_health && (
            <div className="bg-green-900/20 border border-green-800/50 rounded-lg p-3 text-xs text-green-300 space-y-1">
              <p><Monitor className="w-3 h-3 inline mr-1" /> Kdenlive: {bridgeStatus.bridge_health.kdenlive_path}</p>
              <p>Melt: {bridgeStatus.bridge_health.melt_available ? "Disponibile" : "Non trovato"}</p>
              {bridgeStatus.bridge_health.melt_version && <p>Versione: {bridgeStatus.bridge_health.melt_version}</p>}
            </div>
          )}

          <div className="bg-[#0d0d20] border border-gray-800 rounded-lg p-3">
            <p className="text-xs text-gray-400 mb-2">Come configurare:</p>
            <ol className="text-xs text-gray-500 space-y-1 list-decimal ml-4">
              <li>Scarica <a href={`${API}/api/video-editor/bridge/download-script`} className="text-violet-400 hover:underline">kdenlive_bridge.py</a></li>
              <li>Salva in <code className="text-amber-400">D:\</code> e esegui: <code className="text-amber-400">python kdenlive_bridge.py</code></li>
              <li>In un altro terminale: <code className="text-amber-400">ngrok http 5555</code></li>
              <li>Incolla l'URL ngrok qui sopra e clicca Connetti</li>
            </ol>
          </div>

          {error && <div className="text-red-400 text-sm bg-red-900/20 border border-red-800/50 rounded-lg px-4 py-2.5">{error}</div>}
        </CardContent>
      </Card>

      {isConnected && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* AI Editor */}
          <div className="lg:col-span-2 space-y-5">
            <Card className="bg-[#12122a] border-gray-800">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg text-gray-200 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-violet-400" /> Editor AI
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm text-gray-400 mb-1.5 block">Istruzioni (in italiano)</label>
                  <Textarea data-testid="ai-instructions-input" value={instructions} onChange={(e) => setInstructions(e.target.value)}
                    placeholder="Descrivi cosa vuoi fare... Es: 'Taglia i primi 5 secondi del video, aggiungi il logo in alto a destra e scrivi ODR Academy in basso'"
                    className="min-h-[120px] bg-[#0d0d20] border-gray-700 text-white placeholder:text-gray-600 resize-none focus:border-violet-500" />
                </div>

                <Button data-testid="ai-edit-btn" onClick={handleAIEdit} disabled={processing || !instructions.trim()}
                  className="w-full bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 text-white py-3 font-semibold">
                  {processing ? <span className="flex items-center gap-2"><Loader2 className="w-5 h-5 animate-spin" /> AI sta elaborando...</span>
                    : <span className="flex items-center gap-2"><Sparkles className="w-5 h-5" /> Esegui con AI</span>}
                </Button>

                {editResult && (
                  <div data-testid="ai-edit-result" className="bg-[#0d0d20] border border-gray-700 rounded-lg p-4 space-y-3">
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${editResult.status === "ok" ? "bg-green-400" : "bg-red-400"}`} />
                      <span className={`text-sm font-medium ${editResult.status === "ok" ? "text-green-400" : "text-red-400"}`}>
                        {editResult.status === "ok" ? "Completato" : "Errore"}
                      </span>
                    </div>
                    {editResult.ai_interpretation && (
                      <p className="text-xs text-gray-400"><Sparkles className="w-3 h-3 inline text-violet-400 mr-1" /> {editResult.ai_interpretation}</p>
                    )}
                    {editResult.tool_used && (
                      <p className="text-xs text-gray-500">Tool: <code className="text-amber-400">{editResult.tool_used}</code></p>
                    )}
                    {editResult.output?.output_filename && (
                      <p className="text-xs text-green-400">Output: {editResult.output.output_filename} ({editResult.output.size_mb} MB)</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Bridge Files */}
          <div>
            <Card className="bg-[#12122a] border-gray-800">
              <CardHeader className="pb-3 flex flex-row items-center justify-between">
                <CardTitle className="text-lg text-gray-200 flex items-center gap-2">
                  <FileVideo className="w-5 h-5 text-fuchsia-400" /> File Workspace
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={fetchBridgeFiles} className="text-gray-400 hover:text-white h-8 w-8 p-0">
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Aggiungi da PC locale - METODO PRINCIPALE */}
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-amber-400 mb-1.5 font-semibold">Aggiungi da PC (consigliato)</p>
                  <div className="flex gap-1.5">
                    <Input data-testid="local-path-input" value={localPath} onChange={(e) => setLocalPath(e.target.value)}
                      placeholder="D:\Videos\miovideo.mp4"
                      className="bg-[#0d0d20] border-gray-700 text-white text-xs flex-1 focus:border-amber-500 h-9" />
                    <Button onClick={handleCopyLocal} disabled={copying || !localPath.trim()}
                      className="bg-amber-600 hover:bg-amber-700 text-white h-9 px-3 text-xs">
                      {copying ? <Loader2 className="w-3 h-3 animate-spin" /> : "Aggiungi"}
                    </Button>
                  </div>
                  <button onClick={() => handleBrowse(browseDir)}
                    className="text-[10px] text-violet-400 hover:text-violet-300 mt-1 underline">
                    Sfoglia cartelle PC...
                  </button>
                </div>

                {/* File Browser popup */}
                {showBrowser && (
                  <div className="bg-[#0d0d20] border border-gray-700 rounded-lg p-3 max-h-[250px] overflow-y-auto">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-[10px] text-gray-400 truncate max-w-[180px]">{browseDir}</p>
                      <button onClick={() => setShowBrowser(false)} className="text-gray-500 hover:text-white text-xs">X</button>
                    </div>
                    {browseDir !== "D:\\" && (
                      <button onClick={() => handleBrowse(browseDir.split("\\").slice(0, -1).join("\\") || "D:\\")}
                        className="text-xs text-amber-400 hover:underline mb-1 block">.. (su)</button>
                    )}
                    <div className="space-y-0.5">
                      {browseFiles.map((f, i) => (
                        <button key={i} onClick={() => f.is_dir ? handleBrowse(f.path) : selectBrowseFile(f.path)}
                          className={`w-full text-left text-xs px-2 py-1.5 rounded hover:bg-gray-800 flex justify-between ${
                            f.is_dir ? "text-amber-300" : "text-gray-300"
                          }`}>
                          <span className="truncate max-w-[160px]">{f.is_dir ? "📁 " : ""}{f.name}</span>
                          {!f.is_dir && <span className="text-gray-600 ml-1">{f.size_mb}MB</span>}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Upload piccoli file via browser */}
                <label className="flex items-center gap-2 px-4 py-2 bg-[#0d0d20] border border-dashed border-gray-700 rounded-lg cursor-pointer hover:border-gray-600 transition-colors text-xs w-full justify-center">
                  {uploading ? <Loader2 className="w-3 h-3 animate-spin text-gray-400" /> : <Upload className="w-3 h-3 text-gray-500" />}
                  <span className="text-gray-500">{uploading ? "Caricamento..." : "Upload via browser (max 50MB)"}</span>
                  <input type="file" accept="video/*,image/*,audio/*" onChange={handleUpload} className="hidden" disabled={uploading} />
                </label>

                {bridgeFiles.uploads.length > 0 && (
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-gray-600 mb-1.5">Upload</p>
                    <div className="space-y-1.5">
                      {bridgeFiles.uploads.map((f, i) => (
                        <div key={i} className="flex items-center justify-between bg-[#0d0d20] border border-gray-800 rounded px-2.5 py-2 text-xs">
                          <span className="text-gray-300 truncate max-w-[150px]">{f.filename}</span>
                          <span className="text-gray-600 ml-2">{f.size_mb}MB</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {bridgeFiles.outputs.length > 0 && (
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-gray-600 mb-1.5">Output</p>
                    <div className="space-y-1.5">
                      {bridgeFiles.outputs.map((f, i) => (
                        <div key={i} className="flex items-center justify-between bg-[#0d0d20] border border-green-900/30 rounded px-2.5 py-2 text-xs">
                          <span className="text-green-300 truncate max-w-[150px]">{f.filename}</span>
                          <span className="text-gray-600 ml-2">{f.size_mb}MB</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {bridgeFiles.uploads.length === 0 && bridgeFiles.outputs.length === 0 && (
                  <p className="text-gray-500 text-sm text-center py-4">Workspace vuoto</p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}

// ================= MAIN PAGE =================
export default function VideoGeneratorPage() {
  const [activeTab, setActiveTab] = useState("generator");

  const tabs = [
    { id: "generator", label: "Generatore Slide", icon: Film },
    { id: "kdenlive", label: "Editor Kdenlive", icon: Clapperboard },
  ];

  return (
    <div className="min-h-screen bg-[#0a0a1e] text-white p-4 md:p-8" data-testid="video-generator-page">
      <div className="max-w-6xl mx-auto mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2.5 bg-gradient-to-br from-violet-600 to-fuchsia-600 rounded-xl">
            <Film className="w-6 h-6 text-white" />
          </div>
          <h1 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-violet-400 to-fuchsia-400 bg-clip-text text-transparent">
            AI Video Generator PRO
          </h1>
        </div>
        <p className="text-gray-400 text-sm md:text-base ml-14">
          Genera video da testo o edita con Kdenlive + AI
        </p>
      </div>

      {/* Tabs */}
      <div className="max-w-6xl mx-auto mb-6">
        <div className="flex gap-1 bg-[#12122a] p-1 rounded-xl border border-gray-800 w-fit">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button key={tab.id} data-testid={`tab-${tab.id}`} onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  isActive ? "bg-violet-600 text-white shadow-lg shadow-violet-600/30" : "text-gray-400 hover:text-gray-200 hover:bg-white/5"
                }`}>
                <Icon className="w-4 h-4" /> {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="max-w-6xl mx-auto">
        {activeTab === "generator" && <GeneratorTab />}
        {activeTab === "kdenlive" && <KdenliveTab />}
      </div>
    </div>
  );
}
